/**
 * @file Weapon component: a self-contained, controller-agnostic firearm (GDD §7).
 *
 * The weapon owns everything about *using* itself — ammo, cadence, reload, shot
 * validation and the events those produce — and knows nothing about who pulls
 * the trigger. A player controller and an enemy brain call the exact same
 * {@link Weapon#tryFire}; whoever owns the weapon supplies the aim direction and
 * plays its own animations.
 *
 * Two things are deliberately *not* here, because a component must never reach
 * into the world: spawning projectiles and tracing hitscan rays. Those are the
 * shot executor's job, injected by
 * {@link ../systems/WeaponSystem.js WeaponSystem} at equip time. Everything the
 * executor needs — muzzle, direction, damage — arrives as arguments.
 *
 * Every number a weapon needs still lives in one place, so a new weapon remains
 * a new row in {@link ../config/Weapons.js} rather than a new class. Fields fall
 * in two groups: archetype stats, rebuilt from the table on every pickup, and
 * per-instance state, which belongs to *this* gun and survives being dropped on
 * the floor — see {@link Weapon#captureState}.
 */

import * as THREE from 'three';
import { Component } from '../ecs/Component.js';
import { Layer } from '../config/Layers.js';
import { getWeaponArchetype } from '../config/WeaponTuning.js';
import { Events } from '../core/EventBus.js';

/**
 * Outcome of a {@link Weapon#tryFire} request. Owners branch on this instead of
 * inspecting weapon internals.
 * @readonly
 * @enum {string}
 */
export const FireResult = Object.freeze({
  FIRED: 'Fired',
  ON_COOLDOWN: 'On_Cooldown',
  NO_AMMO: 'No_Ammo',
  RELOADING: 'Reloading',
  DISABLED: 'Disabled',
  INVALID_OWNER: 'Invalid_Owner',
  INVALID_DIRECTION: 'Invalid_Direction'
});

/**
 * Internal weapon state. Transitions are the weapon's own business: an owner
 * reads this but never writes it.
 * @readonly
 * @enum {string}
 */
export const WeaponState = Object.freeze({
  READY: 'Ready',
  COOLDOWN: 'Cooldown',
  RELOADING: 'Reloading',
  EMPTY: 'Empty',
  DISABLED: 'Disabled'
});

/**
 * How a shot resolves: an instantly-traced ray, or a travelling entity.
 * @readonly
 * @enum {string}
 */
export const ShotType = Object.freeze({
  HITSCAN: 'hitscan',
  PROJECTILE: 'projectile'
});

/**
 * How the trigger is read. Semi-automatic turns one press into one request;
 * automatic keeps requesting while the trigger is held. Neither can outrun the
 * cadence — that is enforced here, not by the controller.
 * @readonly
 * @enum {string}
 */
export const FireMode = Object.freeze({
  SEMI: 'semi',
  AUTO: 'auto'
});

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const MIN_BURST_INTERVAL = 1e-4;

/**
 * Rolls a fresh weapon's reserve pool: uniform between empty and twice a full
 * magazine. A weapon can land on the floor already dry, which is the whole
 * point — it pairs with the "depleted" red tint on a pickup with nothing left
 * to give.
 * @param {number} magazineCapacity
 * @returns {number}
 */
function rollReserveAmmo(magazineCapacity) {
  const max = Math.floor(magazineCapacity) * 2;
  if (max <= 0) return 0;
  return Math.floor(Math.random() * (max + 1));
}

/**
 * True in development builds. Vite substitutes `import.meta.env` at build time;
 * the `typeof` guard keeps the module importable from plain Node as well.
 */
const DEV_BUILD = typeof import.meta.env === 'undefined' ? true : Boolean(import.meta.env.DEV);

/**
 * @typedef {object} FireOptions
 * @property {number} [accuracyMultiplier] Scales the spread cone: below 1
 *   tightens it, above 1 widens it. Lets two characters share one weapon
 *   resource at different precisions. Never produces a negative cone.
 * @property {number} [rateMultiplier] Scales the fire rate (buffs).
 * @property {number} [damageMultiplier] Scales the damage (buffs).
 * @property {boolean} [unlimitedAmmo] Temporarily skips magazine spending.
 */

/**
 * @callback ShotExecutor
 * @param {Weapon} weapon Weapon that fired.
 * @param {THREE.Vector3} direction Normalised aim direction, before spread.
 * @param {{ damage: number, accuracyMultiplier: number }} context Shot context.
 * @returns {void}
 */

/**
 * The state that belongs to one concrete gun, not to its archetype.
 * @typedef {object} WeaponSnapshot
 * @property {number} cooldown Seconds left before it can fire again.
 * @property {number} currentAmmo Rounds currently loaded in this instance.
 * @property {number} reserveAmmo Spare rounds carried by this instance.
 * @property {boolean} infiniteAmmo Whether this instance ignores its magazine.
 * @property {boolean} infiniteReserve Whether reloads consume reserve ammo.
 */

export class Weapon extends Component {
  /**
   * @param {Partial<import('../config/Weapons.js').WeaponArchetype>} [stats] Weapon stats.
   */
  constructor(stats = {}) {
    super();
    /** @type {string} Display name. */
    this.name = stats.name ?? 'Weapon';
    /** @type {string} Archetype key, so the HUD and pickups can compare loadouts. */
    this.key = stats.key ?? '';
    /** @type {number} 3D held-model scale multiplier. */
    this.modelScale = this._validate('modelScale', stats.modelScale ?? 1, (v) => v > 0, 1);
    /** @type {number} Damage per projectile, before buffs. */
    this.damage = this._validate('damage', stats.damage ?? 10, (v) => v >= 0, 10);
    /** @type {number} Shots per second, before buffs. */
    this.fireRate = this._validate('fireRate', stats.fireRate ?? 5, (v) => v > 0, 5);
    /** @type {number} */
    this.projectileSpeed = stats.projectileSpeed ?? 40;
    /** @type {number} Maximum travel distance, and the hitscan trace length. */
    this.range = this._validate('range', stats.range ?? 30, (v) => v > 0, 30);
    /** @type {number} */
    this.projectileRadius = stats.projectileRadius ?? 0.15;
    /** @type {number} Cone half-angle in radians. */
    this.spread = this._validate('spread', stats.spread ?? 0, (v) => v >= 0, 0);
    /** @type {number} Projectiles released per trigger event. */
    this.pellets = stats.pellets ?? 1;
    /** @type {number} Targets each projectile passes through before expiring. */
    this.pierce = stats.pierce ?? 0;
    /** @type {number} Multiplier on the shove each hit delivers; 0 pushes nothing. */
    this.knockback = stats.knockback ?? 1;
    /** @type {number} Damage ceiling per target per trigger pull; `Infinity` disables it. */
    this.maxDamagePerTarget = stats.maxDamagePerTarget ?? Infinity;
    /** @type {number} Fraction of the range where damage starts dropping, 0..1. */
    this.falloffStart = stats.falloffStart ?? 1;
    /** @type {number} Damage multiplier at maximum range, 0..1. */
    this.falloffMin = stats.falloffMin ?? 1;
    /** @type {number} Muzzle particles per shot; 0 falls back to the global default. */
    this.muzzleParticles = stats.muzzleParticles ?? 0;
    /**
     * Blast carried by each round; null for weapons whose rounds do not
     * explode. The executor spawns a rocket instead of a bullet when set.
     * @type {import('../config/Weapons.js').ExplosiveBlock | null}
     */
    this.explosive = stats.explosive ?? null;
    /** @type {import('../config/Weapons.js').TrailBlock | null} Trail emitted by each round. */
    this.trail = stats.trail ?? null;
    /** @type {number} Seconds a round may stay alive; 0 disables the cap. */
    this.projectileLifetime = stats.projectileLifetime ?? 0;
    /**
     * Continuous cone weapon: nothing is spawned per shot; the stream system
     * owns the trigger and sweeps the cone itself.
     * @type {boolean}
     */
    this.streaming = Boolean(stats.streaming);
    /** @type {number} Burn damage per second applied by the stream. */
    this.burnDamagePerSecond = stats.burnDamagePerSecond ?? 0;
    /** @type {number} Seconds the burn lasts after leaving the stream. */
    this.burnDurationSeconds = stats.burnDurationSeconds ?? 0;
    /** @type {number} Stream ticks since the trigger was pressed. */
    this.streamAccumulator = 0;
    /** @type {number} */
    this.muzzleForward = stats.muzzleForward ?? 1;
    /** @type {number} */
    this.muzzleHeight = stats.muzzleHeight ?? 0.8;
    /** @type {number} */
    this.projectileLayer = stats.projectileLayer ?? Layer.PLAYER_BULLET;
    /** @type {number} Layers a shot from this weapon may hit. */
    this.projectileMask = stats.projectileMask ?? (Layer.WALL | Layer.ENEMY);
    /**
     * Persistent aim-line visual shown while this weapon is equipped.
     * @type {import('../config/Weapons.js').LaserBlock | null}
     */
    this.laser = stats.laser ? { ...stats.laser } : null;
    /** @type {string} Prefab key used for the rounds. */
    this.projectilePrefab = stats.projectile?.prefab ?? 'bullet:pulse';
    /** @type {number} Camera trauma added per shot. */
    this.shake = stats.shake ?? 0;
    /** @type {number} Rounds fired by one accepted trigger request. */
    this.burstCount = this._validate(
      'burstCount',
      stats.burstCount ?? 1,
      (v) => Number.isInteger(v) && v >= 1,
      1
    );
    /** @type {number} Seconds between shots inside a burst. */
    this.burstInterval = this._validate(
      'burstInterval',
      stats.burstInterval ?? 0,
      (v) => Number.isFinite(v) && v >= 0,
      0
    );

    // --- Handgun configuration ---------------------------------------------

    /** @type {boolean} A disabled weapon rejects every request. */
    this.enabled = stats.enabled ?? true;
    /** @type {ShotType} */
    this.shotType = this._validate(
      'shotType',
      stats.shotType ?? ShotType.PROJECTILE,
      (v) => v === ShotType.HITSCAN || v === ShotType.PROJECTILE,
      ShotType.PROJECTILE
    );
    /** @type {FireMode} */
    this.fireMode = this._validate(
      'fireMode',
      stats.fireMode ?? FireMode.SEMI,
      (v) => v === FireMode.SEMI || v === FireMode.AUTO,
      FireMode.SEMI
    );
    /** @type {boolean} When false, shots never damage the owner's own side. */
    this.friendlyFireEnabled = stats.friendlyFireEnabled ?? false;

    // --- Melee, and the metadata that decides how a weapon can leave a hand ---

    /**
     * A melee weapon swings instead of shooting: `MeleeWeaponSystem` is its
     * trigger, exactly as the stream system is a streaming weapon's, and
     * `WeaponSystem` leaves it alone rather than spawning rounds for it.
     * @type {boolean}
     */
    this.melee = stats.type === 'melee';
    /** @type {number} Full width of the swing arc, in degrees. */
    this.meleeArcDegrees = stats.meleeArcDegrees ?? 120;
    /** @type {number} Seconds one swing takes to play out. */
    this.swingSeconds = stats.swingSeconds ?? 0.18;
    /** @type {number} Seconds left on the swing in progress; drives the held pose. */
    this.swingTimer = 0;
    // `fallback` stays a table-only flag: which row plays that role is answered
    // once by `FALLBACK_WEAPON`, so carrying it per instance would only give a
    // future system a second, drift-prone place to ask.
    /** @type {boolean} False for a weapon that must never end up on the floor. */
    this.droppable = stats.droppable !== false;
    /** @type {boolean} False for a weapon `T` cannot throw. */
    this.throwable = stats.throwable !== false;
    /**
     * Cone half-angle in degrees. Derived from `spread` so the radian field
     * stays the single source of truth, but an archetype may override it.
     * @type {number}
     */
    this.spreadDegrees = this._validate(
      'spreadDegrees',
      stats.spreadDegrees ?? this.spread * RAD_TO_DEG,
      (v) => v >= 0,
      0
    );

    // --- Ammunition ---------------------------------------------------------

    /** @type {boolean} Fires forever and never consumes or tracks rounds. */
    this.infiniteAmmo = stats.infiniteAmmo ?? false;
    /** @type {boolean} Reloads may draw forever, while the magazine still empties. */
    this.infiniteReserve = stats.infiniteReserve ?? false;
    /** @type {number} Rounds the magazine holds. */
    this.magazineCapacity = this._validate(
      'magazineCapacity',
      stats.magazineCapacity ?? 12,
      (v) => Number.isFinite(v) && v >= 1,
      12
    );
    /**
     * Rounds loaded when the weapon is created or reset. An over-full magazine
     * is clamped rather than rejected, since the intent ("start loaded") is
     * unambiguous.
     * @type {number}
     */
    this.startingAmmo = this._validate(
      'startingAmmo',
      stats.startingAmmo ?? this.magazineCapacity,
      (v) => Number.isFinite(v) && v >= 0 && v <= this.magazineCapacity,
      Number.isFinite(stats.startingAmmo)
        ? Math.min(Math.max(stats.startingAmmo, 0), this.magazineCapacity)
        : this.magazineCapacity
    );
    const reserveAmmo = this._validate(
      'reserveAmmo',
      stats.reserveAmmo ?? 0,
      (v) => Number.isFinite(v) && v >= 0,
      0
    );
    /** @type {number} Rounds available to reload with. */
    this.reserveAmmo = stats.randomizeReserveAmmo
      ? rollReserveAmmo(this.magazineCapacity)
      : reserveAmmo;
    /** @type {boolean} */
    this.reloadEnabled = stats.reloadEnabled ?? false;
    /** @type {number} Seconds a reload takes. */
    this.reloadDuration = this._validate(
      'reloadDuration',
      stats.reloadDuration ?? 2,
      (v) => Number.isFinite(v) && v >= 0,
      2
    );
    /** @type {boolean} Starts a reload by itself once the magazine runs dry. */
    this.autoReloadWhenEmpty = stats.autoReloadWhenEmpty ?? false;
    /**
     * How the reload reads to the player: which per-round SFX/animation style to
     * use, and whether a final "seated" clack plays. Data only — no system
     * branches on the weapon key. Null for weapons that never reload.
     * @type {import('../config/Weapons.js').ReloadPresentation | null}
     */
    this.reloadPresentation = stats.reloadPresentation ?? null;
    /** @type {number} Rounds currently in the magazine. */
    this.currentAmmo = this.startingAmmo;

    // --- Runtime state ------------------------------------------------------

    /** @type {WeaponState} */
    this.state = WeaponState.DISABLED;
    /** @type {number} Seconds left before the weapon can fire again. */
    this.cooldown = 0;
    /** @type {number} Seconds elapsed into the current reload. */
    this.reloadProgress = 0;
    /** @type {number} Seconds this specific partial reload will take. */
    this.reloadEffectiveDuration = 0;
    /** @type {number} Rounds still queued in the current burst. */
    this.burstShotsRemaining = 0;
    /** @type {number} Seconds until the next queued burst round. */
    this.burstTimer = 0;

    // --- Reload presentation (issue #78) ------------------------------------
    // Real ammo (`currentAmmo`/`reserveAmmo`) is only committed at completion,
    // so cancelling transfers nothing. These fields drive the bala-by-bala
    // preview the HUD, audio and animation read while reloading.

    /** @type {number} Magazine count when the current reload began. */
    this.reloadStartAmmo = 0;
    /** @type {number} Reserve count when the current reload began. */
    this.reloadStartReserve = 0;
    /** @type {number} Magazine count the reload is filling toward. */
    this.reloadTargetAmmo = 0;
    /** @type {number} Rounds this reload will present in total. */
    this.reloadRoundsTotal = 0;
    /** @type {number} Rounds presented so far this reload. */
    this.reloadRoundsPresented = 0;
    /** @type {boolean} Set by controllers; read once per frame by the weapon system. */
    this.triggerHeld = false;
    /** @type {boolean} Trigger state last frame, so semi-auto can find the edge. */
    this.triggerWasHeld = false;
    /** @type {import('../ecs/Entity.js').Entity | null} Entity carrying the weapon. */
    this.owner = null;
    /** @type {THREE.Vector3} World-space muzzle, refreshed by the weapon system. */
    this.muzzle = new THREE.Vector3();
    /** @type {THREE.Vector3} Normalised world-space aim direction, refreshed by the weapon system. */
    this.aimDirection = new THREE.Vector3(0, 0, 1);
    /** @type {number} Seconds the weapon has been ticking, used for shot stamps. */
    this.elapsed = 0;
    /** @type {number} Value of {@link Weapon#elapsed} at the last valid shot. */
    this.lastShotTime = -Infinity;

    // --- Debug readouts -----------------------------------------------------

    /** @type {FireResult | null} Result of the most recent request. */
    this.lastFireResult = null;
    /** @type {THREE.Vector3} Direction of the most recent shot. */
    this.lastShotDirection = new THREE.Vector3();
    /** @type {THREE.Vector3} Impact point of the most recent hit. */
    this.lastHitPosition = new THREE.Vector3();
    /** @type {import('../ecs/Entity.js').Entity | null} What the last shot hit, if anything. */
    this.lastHitTarget = null;
    /** @type {number} Damage dealt by the most recent hit. */
    this.lastDamageDealt = 0;

    /** @type {import('../core/EventBus.js').EventBus | null} @private */
    this._events = null;
    /** @type {ShotExecutor | null} @private */
    this._executor = null;
    /** @type {THREE.Vector3} Scratch for direction normalisation. @private */
    this._aim = new THREE.Vector3();
    /** @type {THREE.Vector3} Aim locked in for queued burst rounds. @private */
    this._burstDirection = new THREE.Vector3();
    /** @type {Required<FireOptions>} Per-burst modifiers. @private */
    this._burstOptions = {
      accuracyMultiplier: 1,
      rateMultiplier: 1,
      damageMultiplier: 1,
      unlimitedAmmo: false
    };
  }

  /**
   * Builds a weapon from the archetype table.
   *
   * @param {string} key Key into {@link ../config/Weapons.js WEAPONS}.
   * @param {Partial<import('../config/Weapons.js').WeaponArchetype>} [overrides] Per-spawn overrides.
   * @returns {Weapon}
   */
  static fromArchetype(key, overrides = {}) {
    const archetype = getWeaponArchetype(key);
    if (!archetype) throw new ReferenceError(`Unknown weapon archetype "${key}".`);
    return new Weapon({
      ...archetype,
      ...overrides,
      key,
      randomizeReserveAmmo: overrides.randomizeReserveAmmo ?? true
    });
  }

  /**
   * Snapshots what belongs to this particular gun rather than to its archetype.
   * A dropped weapon carries the snapshot on its pickup, so swapping two guns of
   * the same type never merges or resets their state. Ammo goes here the day it
   * exists — one field, no other file changes.
   *
   * `triggerHeld` is deliberately absent: it is controller intent, not gun
   * state, and a gun lying on the floor is not being fired.
   *
   * @returns {WeaponSnapshot}
   */
  captureState() {
    return {
      cooldown: this.cooldown,
      currentAmmo: this.currentAmmo,
      reserveAmmo: this.reserveAmmo,
      infiniteAmmo: this.infiniteAmmo,
      infiniteReserve: this.infiniteReserve
    };
  }

  /**
   * Restores a snapshot taken by {@link Weapon#captureState}.
   * @param {WeaponSnapshot | null} [state] Snapshot, or null for a factory-fresh gun.
   * @returns {Weapon} This weapon, for chaining.
   */
  applyState(state) {
    if (!state) return this;
    this.cooldown = state.cooldown ?? 0;
    this.currentAmmo = Math.min(
      Math.max(state.currentAmmo ?? this.currentAmmo, 0),
      this.magazineCapacity
    );
    this.reserveAmmo = Math.max(0, state.reserveAmmo ?? this.reserveAmmo);
    this.infiniteAmmo = state.infiniteAmmo ?? this.infiniteAmmo;
    this.infiniteReserve = state.infiniteReserve ?? this.infiniteReserve;
    this._cancelBurst();
    return this;
  }

  /** @returns {number} Seconds between shots at the base fire rate. */
  get shotInterval() {
    return 1 / this.fireRate;
  }

  /**
   * Whether the stat block can actually put rounds downrange. Enemies
   * scavenging the floor use this to ignore weapons that would leave them
   * harmless.
   * @returns {boolean}
   */
  get isLoaded() {
    return this.damage > 0 && this.fireRate > 0 && this.pellets > 0;
  }

  // -------------------------------------------------------------- equipment

  /**
   * Binds the weapon to an owner. The owner supplies the services the weapon
   * cannot reach on its own: an event bus and a shot executor.
   *
   * @param {import('../ecs/Entity.js').Entity} owner Entity that carries it.
   * @param {object} [services] Injected services.
   * @param {import('../core/EventBus.js').EventBus} [services.events] Event bus.
   * @param {ShotExecutor} [services.executor] Spawns the projectile or traces the ray.
   * @returns {Weapon} This weapon, so factories can chain `entity.add(...)`.
   */
  equip(owner, { events = null, executor = null } = {}) {
    this.owner = owner ?? null;
    if (events) this._events = events;
    if (executor) this._executor = executor;

    this.cooldown = 0;
    this.reloadProgress = 0;
    this.reloadEffectiveDuration = 0;
    this._cancelBurst();
    this._resetReloadPresentation();
    this.triggerHeld = false;
    this.triggerWasHeld = false;
    this.lastFireResult = null;
    this.lastShotTime = -Infinity;
    this.currentAmmo = Math.min(Math.max(this.currentAmmo, 0), this.magazineCapacity);

    this._refreshState();
    this._emit(Events.WEAPON_EQUIPPED, { weapon: this, owner: this.owner });
    return this;
  }

  /**
   * Whether a shot executor has been injected. Factories equip with owner
   * only; the weapon system re-equips with services when this is false.
   * @returns {boolean}
   */
  get hasExecutor() {
    return Boolean(this._executor);
  }

  /**
   * Releases the weapon. Any reload in flight is cancelled, per spec, so a
   * dropped weapon cannot finish reloading in someone else's hands.
   * @returns {void}
   */
  unequip() {
    const previousOwner = this.owner;
    if (this.state === WeaponState.RELOADING) this.cancelReload();

    this.owner = null;
    this.triggerHeld = false;
    this.triggerWasHeld = false;
    this._cancelBurst();
    this.state = WeaponState.DISABLED;
    this._emit(Events.WEAPON_UNEQUIPPED, { weapon: this, owner: previousOwner });
  }

  /**
   * Enables or disables the weapon. A disabled weapon rejects every request
   * without touching its ammo.
   * @param {boolean} enabled Whether the weapon may be used.
   * @returns {void}
   */
  setEnabled(enabled) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled && this.state === WeaponState.RELOADING) this.cancelReload();
    if (!enabled) this._cancelBurst();
    this._refreshState();
    this._emit(enabled ? Events.WEAPON_ENABLED : Events.WEAPON_DISABLED, { weapon: this });
  }

  // ------------------------------------------------------------------ firing

  /**
   * Requests a shot along a direction. Safe to call every frame from anywhere:
   * the weapon decides, and a rejected request costs nothing.
   *
   * @param {{ x: number, y: number, z: number }} direction Aim direction; need
   *   not be normalised.
   * @param {FireOptions} [options] Per-shot modifiers supplied by the owner.
   * @returns {FireResult}
   */
  tryFire(direction, options = {}) {
    if (!this.enabled) return this._result(FireResult.DISABLED);
    if (!this.owner || this.owner.alive === false) return this._result(FireResult.INVALID_OWNER);
    const unlimitedAmmo = this.infiniteAmmo || Boolean(options.unlimitedAmmo);
    if (this.state === WeaponState.RELOADING) {
      if (unlimitedAmmo) {
        this.cancelReload();
      } else if (this.reloadPresentation?.style === 'single_round') {
        // Single-round weapons (shotgun/railgun) can cut the reload short and
        // shoot with whatever has already been seated (issue #93).
        this._interruptReloadForFire();
      } else if (this.currentAmmo > 0) {
        // Magazine reloads do not seat rounds until completion, but the rounds
        // that were still in the weapon when reload began remain fireable.
        this._interruptReloadForFire({ commitPresented: false });
      } else {
        return this._result(FireResult.RELOADING);
      }
    }
    if (this.burstShotsRemaining > 0) return this._result(FireResult.ON_COOLDOWN);
    if (this.cooldown > 0) return this._result(FireResult.ON_COOLDOWN);

    if (!unlimitedAmmo && this.currentAmmo <= 0) {
      // Dry-fire is still a cadence-limited "shot attempt": without this, AUTO
      // mode would emit empty-trigger every frame while the trigger is held and
      // any empty-click SFX would hard-stutter. Rejected mid-cooldown requests
      // still return ON_COOLDOWN above and do not extend the timer.
      this._emit(Events.WEAPON_EMPTY_TRIGGER, { weapon: this, owner: this.owner });
      if (this.autoReloadWhenEmpty && this.reload()) {
        return this._result(FireResult.NO_AMMO);
      }
      this.cooldown += this.shotInterval / Math.max(options.rateMultiplier ?? 1, 1e-3);
      this._refreshState();
      return this._result(FireResult.NO_AMMO);
    }

    if (!this._normaliseDirection(direction)) return this._result(FireResult.INVALID_DIRECTION);
    // A weapon with no executor has no muzzle to fire from — it is carried but
    // not wired up, which is an owner problem rather than an ammo problem.
    if (!this._executor) return this._result(FireResult.INVALID_OWNER);

    this._executeFire(this._aim, options);
    this._queueBurst(this._aim, options);
    return this._result(FireResult.FIRED);
  }

  /**
   * Requests a shot at a world position instead of a direction, which is what
   * an AI usually has: the weapon converts it against its own muzzle.
   *
   * @param {{ x: number, y: number, z: number }} target Target world position.
   * @param {FireOptions} [options] Per-shot modifiers.
   * @returns {FireResult}
   */
  tryFireAt(target, options = {}) {
    if (!target) return this._result(FireResult.INVALID_DIRECTION);
    this._aim.set(target.x - this.muzzle.x, 0, target.z - this.muzzle.z);
    return this.tryFire(this._aim, options);
  }

  /**
   * @returns {boolean} Whether a request with a valid direction would fire now.
   */
  canFire() {
    return (
      this.enabled &&
      Boolean(this.owner) &&
      this.owner.alive !== false &&
      Boolean(this._executor) &&
      this.state !== WeaponState.RELOADING &&
      this.burstShotsRemaining <= 0 &&
      this.cooldown <= 0 &&
      (this.infiniteAmmo || this.currentAmmo > 0)
    );
  }

  /**
   * Applies the weapon's cone to an aim direction. Exposed so the shot executor
   * can deviate each pellet independently while the dispersion rule stays here.
   *
   * @param {THREE.Vector3} direction Normalised aim direction.
   * @param {THREE.Vector3} out Result, mutated in place.
   * @param {number} [accuracyMultiplier] Cone scale; clamped to zero or above.
   * @returns {THREE.Vector3} The same `out` vector.
   */
  applySpread(direction, out, accuracyMultiplier = 1) {
    const cone = Math.max(0, this.spreadDegrees * DEG_TO_RAD) * Math.max(0, accuracyMultiplier);
    const base = Math.atan2(direction.x, direction.z);
    const angle = cone === 0 ? base : base + (Math.random() - 0.5) * 2 * cone;
    return out.set(Math.sin(angle), 0, Math.cos(angle));
  }

  // ---------------------------------------------------------------- reloading

  /**
   * Starts a reload. Returns whether one actually began, so an owner can react
   * without inspecting state.
   * @returns {boolean}
   */
  reload() {
    if (!this.reloadEnabled || !this.enabled || this.infiniteAmmo) return false;
    if (this.state === WeaponState.RELOADING) return false;
    if (this.currentAmmo >= this.magazineCapacity) return false;
    if (!this.infiniteReserve && this.reserveAmmo <= 0) return false;

    this._cancelBurst();
    this.state = WeaponState.RELOADING;
    this.reloadProgress = 0;

    // Snapshot the presentation targets from the real ammo. The transfer is
    // recomputed identically at completion; because firing is blocked while
    // reloading, neither `currentAmmo` nor `reserveAmmo` can drift in between,
    // so the round count presented always matches the count committed.
    const missing = this.magazineCapacity - this.currentAmmo;
    const transfer = this.infiniteReserve ? missing : Math.min(missing, this.reserveAmmo);
    this.reloadStartAmmo = this.currentAmmo;
    this.reloadStartReserve = this.reserveAmmo;
    this.reloadTargetAmmo = this.currentAmmo + transfer;
    this.reloadRoundsTotal = transfer;
    this.reloadRoundsPresented = 0;
    this.reloadEffectiveDuration = this.reloadDuration * (transfer / this.magazineCapacity);

    this._emit(Events.RELOAD_STARTED, { weapon: this, owner: this.owner });
    // A zero-length reload must still complete rather than hang in Reloading.
    if (this.reloadEffectiveDuration <= 0) this._completeReload();
    return true;
  }

  /**
   * Abandons a reload in progress. No ammo moves and the accumulated progress
   * is lost; the weapon can fire again immediately if the magazine is not empty.
   * @returns {void}
   */
  cancelReload() {
    if (this.state !== WeaponState.RELOADING) return;
    this.reloadProgress = 0;
    this.reloadEffectiveDuration = 0;
    // No ammo moves and the pending presentation is dropped, so no further
    // round-loaded ticks fire and the HUD falls back to the real counts.
    this._resetReloadPresentation();
    this._refreshState();
    this._emit(Events.RELOAD_CANCELLED, { weapon: this, owner: this.owner });
  }

  /**
   * Cuts a reload short so the carrier can fire. Single-round reloads commit
   * already seated rounds; magazine reloads keep only the rounds that were
   * already in the weapon when reload began.
   * @param {{ commitPresented?: boolean }} [options] Whether previewed rounds
   *   become real ammo before the shot attempt continues.
   * @returns {void}
   * @private
   */
  _interruptReloadForFire({ commitPresented = true } = {}) {
    if (this.state !== WeaponState.RELOADING) return;

    const presented = commitPresented ? this.reloadRoundsPresented : 0;
    this.currentAmmo = this.reloadStartAmmo + presented;
    this.reserveAmmo = this.infiniteReserve
      ? this.reloadStartReserve
      : this.reloadStartReserve - presented;
    this.reloadProgress = 0;
    this.reloadEffectiveDuration = 0;
    this._resetReloadPresentation();
    this._refreshState();

    this._emit(Events.AMMO_CHANGED, {
      weapon: this,
      current: this.currentAmmo,
      capacity: this.magazineCapacity,
      reserve: this.reserveAmmo
    });
    this._emit(Events.RELOAD_INTERRUPTED, {
      weapon: this,
      owner: this.owner,
      current: this.currentAmmo,
      reserve: this.reserveAmmo
    });
  }

  // ------------------------------------------------------------------ queries

  /** @returns {number} */
  getCurrentAmmo() {
    return this.currentAmmo;
  }

  /** @returns {number} */
  getMagazineCapacity() {
    return this.magazineCapacity;
  }

  /** @returns {number} */
  getReserveAmmo() {
    return this.reserveAmmo;
  }

  /** @returns {boolean} */
  isReloading() {
    return this.state === WeaponState.RELOADING;
  }

  /** @returns {number} Reload completion in the 0..1 range. */
  getReloadProgress() {
    if (this.state !== WeaponState.RELOADING) return 0;
    if (this.reloadEffectiveDuration <= 0) return 1;
    return Math.min(1, this.reloadProgress / this.reloadEffectiveDuration);
  }

  /**
   * The discrete magazine readout for the HUD (issue #78). While reloading it
   * reports the presentation counts — the magazine filling in whole rounds and
   * the reserve draining to match — so the HUD never has to show a percentage
   * or reach into weapon internals. Otherwise it reports the real ammo.
   *
   * @returns {{ current: number, capacity: number, reserve: number,
   *   roundsLoaded: number, roundsTotal: number }}
   */
  getReloadPreviewAmmo() {
    if (this.state === WeaponState.RELOADING) {
      return {
        current: this.reloadStartAmmo + this.reloadRoundsPresented,
        capacity: this.magazineCapacity,
        reserve: this.infiniteReserve
          ? this.reloadStartReserve
          : this.reloadStartReserve - this.reloadRoundsPresented,
        roundsLoaded: this.reloadRoundsPresented,
        roundsTotal: this.reloadRoundsTotal
      };
    }
    return {
      current: this.currentAmmo,
      capacity: this.magazineCapacity,
      reserve: this.reserveAmmo,
      roundsLoaded: 0,
      roundsTotal: 0
    };
  }

  /** @returns {number} Seconds until the next shot is allowed. */
  getRemainingShotCooldown() {
    return Math.max(0, this.cooldown);
  }

  // ------------------------------------------------------------------- ticking

  /**
   * Advances cadence and reload timers. Driven by
   * {@link ../systems/WeaponSystem.js WeaponSystem} so the weapon never depends
   * on a clock of its own — which is what keeps cadence framerate-independent.
   *
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    this.elapsed += dt;

    // Only decremented while positive, so at most one frame of overshoot is
    // ever carried into the next interval.
    if (this.cooldown > 0) this.cooldown -= dt;

    if (this.state === WeaponState.RELOADING) {
      this.reloadProgress += dt;
      // Present whole rounds up to the fraction elapsed, then commit once the
      // timer runs out. The final round lands as the reload completes.
      this._presentReloadRounds();
      if (this.reloadProgress >= this.reloadEffectiveDuration) this._completeReload();
      return;
    }

    this._updateBurst(dt);
    if (this.state === WeaponState.RELOADING) return;
    this._refreshState();
  }

  /**
   * Restores the weapon to a freshly-built state, for pooling and run restarts.
   * @returns {void}
   */
  reset() {
    this.currentAmmo = this.startingAmmo;
    this.cooldown = 0;
    this.reloadProgress = 0;
    this.reloadEffectiveDuration = 0;
    this._cancelBurst();
    this._resetReloadPresentation();
    this.triggerHeld = false;
    this.triggerWasHeld = false;
    this.elapsed = 0;
    this.lastShotTime = -Infinity;
    this.lastFireResult = null;
    this.lastHitTarget = null;
    this.lastDamageDealt = 0;
    this.aimDirection.set(0, 0, 1);
    this.owner = null;
    this.state = WeaponState.DISABLED;
  }

  // ------------------------------------------------------------------ internal

  /**
   * The shot itself, in the order the spec lays out: spend the round, stamp the
   * time, resolve the shot, then announce it and start the cooldown.
   *
   * @param {THREE.Vector3} direction Normalised aim direction.
   * @param {FireOptions} options Per-shot modifiers.
   * @returns {void}
   * @private
   */
  _executeFire(direction, options, cooldownSeconds = null) {
    const rateMultiplier = options.rateMultiplier ?? 1;
    const damageMultiplier = options.damageMultiplier ?? 1;
    const accuracyMultiplier = options.accuracyMultiplier ?? 1;
    const unlimitedAmmo = this.infiniteAmmo || Boolean(options.unlimitedAmmo);

    if (!unlimitedAmmo) {
      this.currentAmmo--;
      this._emit(Events.AMMO_CHANGED, {
        weapon: this,
        current: this.currentAmmo,
        capacity: this.magazineCapacity,
        reserve: this.reserveAmmo
      });
    }

    this.lastShotTime = this.elapsed;
    this.lastShotDirection.copy(direction);
    this.lastHitTarget = null;
    this.lastDamageDealt = 0;

    this._executor(this, direction, {
      damage: this.damage * damageMultiplier,
      accuracyMultiplier
    });

    this._emit(Events.WEAPON_FIRED, {
      position: this.muzzle,
      direction,
      owner: this.owner,
      weapon: this,
      muzzleCount: this.muzzleParticles
    });

    // Carrying the remainder forward keeps the cadence exact regardless of
    // frame rate, instead of silently capping fire rate at 1 shot per frame.
    this.cooldown +=
      cooldownSeconds ?? this.shotInterval / Math.max(rateMultiplier, 1e-3);

    if (!unlimitedAmmo && this.currentAmmo <= 0) {
      this._emit(Events.WEAPON_EMPTY, { weapon: this, owner: this.owner });
      // reload() already entered Reloading — leave that state alone.
      if (this.autoReloadWhenEmpty && this.reload()) return;
    }
    this._refreshState();
  }

  /**
   * Queues the remaining shots of a burst after the first round has fired.
   * @param {THREE.Vector3} direction Normalised aim direction.
   * @param {FireOptions} options Per-shot modifiers.
   * @returns {void}
   * @private
   */
  _queueBurst(direction, options) {
    if (this.burstCount <= 1 || !this._canContinueBurst()) {
      this._cancelBurst();
      return;
    }

    this.burstShotsRemaining = this.burstCount - 1;
    this.burstTimer = Math.max(this.burstInterval, MIN_BURST_INTERVAL);
    this._burstDirection.copy(direction);
    this._burstOptions.accuracyMultiplier = options.accuracyMultiplier ?? 1;
    this._burstOptions.rateMultiplier = options.rateMultiplier ?? 1;
    this._burstOptions.damageMultiplier = options.damageMultiplier ?? 1;
    this._burstOptions.unlimitedAmmo = Boolean(options.unlimitedAmmo);
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _updateBurst(dt) {
    if (this.burstShotsRemaining <= 0) return;
    if (!this._canContinueBurst()) {
      this._cancelBurst();
      return;
    }

    this.burstTimer -= dt;
    while (this.burstShotsRemaining > 0 && this.burstTimer <= 0) {
      if (!this._canContinueBurst()) {
        this._cancelBurst();
        return;
      }

      this.burstShotsRemaining--;
      this._executeFire(this._burstDirection, this._burstOptions, 0);

      if (this.burstShotsRemaining <= 0 || !this._canContinueBurst()) {
        this._cancelBurst();
        return;
      }
      this.burstTimer += Math.max(this.burstInterval, MIN_BURST_INTERVAL);
    }
  }

  /**
   * @returns {boolean} Whether a queued burst round may still leave the weapon.
   * @private
   */
  _canContinueBurst() {
    return (
      this.enabled &&
      Boolean(this.owner) &&
      this.owner.alive !== false &&
      Boolean(this._executor) &&
      this.state !== WeaponState.RELOADING &&
      (this.infiniteAmmo || this._burstOptions.unlimitedAmmo || this.currentAmmo > 0)
    );
  }

  /**
   * @returns {void}
   * @private
   */
  _cancelBurst() {
    this.burstShotsRemaining = 0;
    this.burstTimer = 0;
  }

  /**
   * @returns {void}
   * @private
   */
  _completeReload() {
    // Emit any rounds the tick cadence has not yet presented, so exactly one
    // RELOAD_ROUND_LOADED fires per unit before the magazine is committed.
    this._presentReloadRoundsUpTo(this.reloadRoundsTotal);

    const missing = this.magazineCapacity - this.currentAmmo;
    const transferred = this.infiniteReserve ? missing : Math.min(missing, this.reserveAmmo);
    this.currentAmmo += transferred;
    if (!this.infiniteReserve) this.reserveAmmo -= transferred;
    this.reloadProgress = 0;
    this.reloadEffectiveDuration = 0;
    this._resetReloadPresentation();
    this._refreshState();

    this._emit(Events.AMMO_CHANGED, {
      weapon: this,
      current: this.currentAmmo,
      capacity: this.magazineCapacity,
      reserve: this.reserveAmmo
    });
    this._emit(Events.RELOAD_COMPLETED, {
      weapon: this,
      owner: this.owner,
      current: this.currentAmmo,
      reserve: this.reserveAmmo
    });
  }

  /**
   * Presents whole rounds up to however many the elapsed fraction of the reload
   * has earned. Floors so the count only ever advances by whole units; the last
   * round is reached exactly as `reloadProgress` hits the effective duration.
   * @returns {void}
   * @private
   */
  _presentReloadRounds() {
    if (this.reloadRoundsTotal <= 0) return;
    const fraction =
      this.reloadEffectiveDuration <= 0
        ? 1
        : Math.min(1, this.reloadProgress / this.reloadEffectiveDuration);
    this._presentReloadRoundsUpTo(Math.floor(fraction * this.reloadRoundsTotal));
  }

  /**
   * Advances the presented-round count to `target`, emitting one
   * {@link Events.RELOAD_ROUND_LOADED} per newly-seated round with the preview
   * counts already computed for listeners.
   * @param {number} target Rounds that should be presented by now.
   * @returns {void}
   * @private
   */
  _presentReloadRoundsUpTo(target) {
    const clamped = Math.min(this.reloadRoundsTotal, target);
    while (this.reloadRoundsPresented < clamped) {
      this.reloadRoundsPresented++;
      this._emit(Events.RELOAD_ROUND_LOADED, {
        weapon: this,
        owner: this.owner,
        current: this.reloadStartAmmo + this.reloadRoundsPresented,
        capacity: this.magazineCapacity,
        reserve: this.infiniteReserve
          ? this.reloadStartReserve
          : this.reloadStartReserve - this.reloadRoundsPresented,
        roundIndex: this.reloadRoundsPresented,
        roundsTotal: this.reloadRoundsTotal,
        style: this.reloadPresentation?.style ?? 'magazine'
      });
    }
  }

  /**
   * Clears the bala-by-bala presentation back to a neutral, not-reloading state.
   * @returns {void}
   * @private
   */
  _resetReloadPresentation() {
    this.reloadStartAmmo = 0;
    this.reloadStartReserve = 0;
    this.reloadTargetAmmo = 0;
    this.reloadRoundsTotal = 0;
    this.reloadRoundsPresented = 0;
  }

  /**
   * Recomputes the state machine from the facts. Deriving it in one place means
   * no transition can be forgotten at a call site.
   * @returns {void}
   * @private
   */
  _refreshState() {
    if (!this.enabled || !this.owner || this.owner.alive === false) {
      this.state = WeaponState.DISABLED;
      return;
    }
    if (!this.infiniteAmmo && this.currentAmmo <= 0) {
      this.state = WeaponState.EMPTY;
      return;
    }
    this.state = this.cooldown > 0 ? WeaponState.COOLDOWN : WeaponState.READY;
  }

  /**
   * Normalises an aim direction onto the XZ plane into the scratch vector.
   * @param {{ x: number, y: number, z: number }} direction Requested direction.
   * @returns {boolean} False when the direction carries no usable heading.
   * @private
   */
  _normaliseDirection(direction) {
    if (!direction) return false;
    const x = direction.x;
    const z = direction.z;
    if (!Number.isFinite(x) || !Number.isFinite(z)) return false;

    const lengthSq = x * x + z * z;
    if (lengthSq < 1e-8) return false;

    const inverse = 1 / Math.sqrt(lengthSq);
    this._aim.set(x * inverse, 0, z * inverse);
    return true;
  }

  /**
   * @param {FireResult} result Outcome to record and return.
   * @returns {FireResult} The same result.
   * @private
   */
  _result(result) {
    this.lastFireResult = result;
    return result;
  }

  /**
   * @param {string} type Event name.
   * @param {object} payload Reused payload; listeners must copy what they keep.
   * @returns {void}
   * @private
   */
  _emit(type, payload) {
    this._events?.emit(type, payload);
  }

  /**
   * Guards one configured value, warning in development builds with everything
   * needed to find the offending row: instance, parameter, value, fallback.
   *
   * @template T
   * @param {string} parameter Field name.
   * @param {T} value Configured value.
   * @param {(value: T) => boolean} isValid Predicate the value must satisfy.
   * @param {T} fallback Safe value used when the check fails.
   * @returns {T} `value` when valid, `fallback` otherwise.
   * @private
   */
  _validate(parameter, value, isValid, fallback) {
    if (isValid(value)) return value;
    if (DEV_BUILD) {
      console.warn(
        `[Weapon "${this.name}"] invalid ${parameter}: received ${JSON.stringify(value)}, ` +
          `using ${JSON.stringify(fallback)} instead.`
      );
    }
    return fallback;
  }
}
